import {
  useMemo, useCallback, useRef,
} from 'react'
import { Bell } from 'lucide-react'
import Link from 'next/link'
import useSWR from 'swr'
import { HEADER_NOTIFICATIONS_KEY } from '@/constants/notifications'
import { trackMxEvent } from '@/utils/mixpanel'
import useAuth from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

import NotificationItem from './notification'
import type { TNovuMessage } from '@/interfaces/notification'

type NotificationsResponse = {
  data: TNovuMessage[]
  hasMore: boolean
}

const NotificationMenu = () => {
  const { currentUser } = useAuth()
  const { data, mutate } = useSWR<NotificationsResponse>(
    currentUser ? HEADER_NOTIFICATIONS_KEY : null,
    (url: string) => fetch(new URL(url, window.location.origin).href).then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    }),
    { refreshInterval: 30000 },
  )

  const isMarkingRef = useRef(false)

  const notifications = data?.data

  const onClick = useCallback((notif: TNovuMessage) => {
    trackMxEvent('Header Notification Clicked', { read: notif.read })
  }, [])

  const [ notifList, hasUnseen ] = useMemo(() => {
    if (!notifications) {
      return [[], false ]
    }

    const sorted = [ ...notifications ].sort((a, b) => {
      if (a.seen) return 1
      return b.seen ? -1 : 0
    })

    const sliceList = sorted.slice(0, 5)

    return [
      sliceList,
      sliceList.findIndex((notif) => !notif.seen) !== -1,
    ]
  }, [ notifications ])

  const onOpenChange = useCallback(async (isOpen: boolean) => {
    if (!isOpen) return
    trackMxEvent('Header Notification Shown')
    if (!hasUnseen || isMarkingRef.current) return

    isMarkingRef.current = true
    try {
      const res = await fetch('/api/notifications/mark-as-seen', { method: 'POST' })
      if (!res.ok) return
      // 楽観的にキャッシュを seen: true に書き換えて赤バッジを即時消去
      await mutate(
        (current) => {
          if (!current) return current
          const now = new Date().toISOString()
          return {
            ...current,
            data: current.data.map((n) => (
              n.seen ? n : { ...n, seen: true, lastSeenDate: now }
            )),
          }
        },
        { revalidate: false },
      )
    }
    finally {
      isMarkingRef.current = false
    }
  }, [ hasUnseen, mutate ])

  return (
    <DropdownMenu onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icons" className="relative">
          <Bell size={24} />
          {hasUnseen && (
            <div className="absolute top-2 right-2">
              <div className="h-2 w-2 rounded-full border border-white bg-destructive dark:border-black" />
            </div>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-full max-w-sm">
        <DropdownMenuLabel>お知らせ</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {notifList.length > 0 ? (
          <DropdownMenuGroup className="divide-y">
            {notifList.map((notif) => (
              <DropdownMenuItem key={notif._id} asChild>
                <Link
                  href={notif.payload.linkHref || '/'}
                  onClick={() => onClick(notif)}
                >
                  <NotificationItem notification={notif} />
                </Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
        ) : (
          <DropdownMenuLabel className="p-10 text-center">
            <p className="mb-0">お知らせはありません</p>
          </DropdownMenuLabel>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link
            href="/notifications"
            className="block justify-center"
            onClick={() => trackMxEvent('Header Notification List Click')}
          >
            すべて見る
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default NotificationMenu
